import { useState, useEffect, useRef } from "react";
import PageLayout from "examples/LayoutContainers/PageLayout";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import Footer from "layouts/authentication/components/Footer";
import SkipToContent from "components/SkipToContent";
import usePageTitle from "utils/usePageTitle";
import pageRoutes from "page.routes";

// MUI Components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";

import useAPI from "../../../../utils/useApi";
import TurnstileWidget, { TURNSTILE_RESPONSE_FIELD } from "components/TurnstileWidget";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../formular-sesizare/customToast.css';

const formatDate = (raw) => {
  if (!raw) return "—";
  try {
    return new Intl.DateTimeFormat("ro-RO", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    }).format(new Date(raw));
  } catch {
    return raw;
  }
};

const PALETTE = {
  navy: "#061D36",
  navyMid: "#0f2c4a",
  navyBtn: "#1a3a5c",
  blueAccent: "#1565C0",
  yellow: "#FFC107",
  yellowHover: "#FFB300",
  title: "#0f2c4a",
  body: "#5a6b85",
  muted: "#8b9cb0",
  border: "rgba(6, 29, 54, 0.08)",
  surface: "#f8f9fb",
};

const primaryBtnSx = {
  background: `${PALETTE.yellow} !important`,
  bgcolor: `${PALETTE.yellow} !important`,
  color: `${PALETTE.navy} !important`,
  fontWeight: 700,
  boxShadow: "0 4px 14px rgba(255,193,7,0.25) !important",
  "&:hover": {
    background: `${PALETTE.yellowHover} !important`,
    bgcolor: `${PALETTE.yellowHover} !important`,
    boxShadow: "0 6px 18px rgba(255,193,7,0.35) !important",
  },
  "&:focus, &:focus:not(:hover), &.Mui-focusVisible": {
    background: `${PALETTE.yellow} !important`,
    bgcolor: `${PALETTE.yellow} !important`,
    color: `${PALETTE.navy} !important`,
    boxShadow: "0 4px 14px rgba(255,193,7,0.25) !important",
    outline: "none",
  },
};

const navyBtnSx = {
  background: `${PALETTE.navyBtn} !important`,
  bgcolor: `${PALETTE.navyBtn} !important`,
  color: "#fff !important",
  fontWeight: 700,
  boxShadow: "none !important",
  "&:hover": {
    background: "#234a72 !important",
    bgcolor: "#234a72 !important",
    boxShadow: "none !important",
  },
  "&:focus, &:focus:not(:hover), &.Mui-focusVisible": {
    background: `${PALETTE.navyBtn} !important`,
    bgcolor: `${PALETTE.navyBtn} !important`,
    color: "#fff !important",
    boxShadow: "none !important",
    outline: "none",
  },
};

const cardSx = {
  mb: 4,
  p: 2,
  bgcolor: PALETTE.surface,
  border: `1px solid ${PALETTE.border}`,
  borderRadius: "8px",
};

const sectionCardSx = {
  mb: 4,
  p: 2.5,
  bgcolor: PALETTE.surface,
  border: `1px solid ${PALETTE.border}`,
  borderRadius: "8px",
};

const downloadBtnSx = {
  bgcolor: "white",
  color: PALETTE.blueAccent,
  borderColor: PALETTE.blueAccent,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8rem",
  px: 2,
  py: 0.75,
  minWidth: "100px",
  borderRadius: "6px",
  boxShadow: "none",
  "&:hover": {
    bgcolor: "rgba(21, 101, 192, 0.08)",
    borderColor: "#0d47a1",
    color: "#0d47a1",
    boxShadow: "0 2px 8px rgba(21, 101, 192, 0.15)",
  },
};

const infoAlertSx = {
  bgcolor: "rgba(21, 101, 192, 0.08)",
  color: PALETTE.navyMid,
  "& .MuiAlert-icon": { color: PALETTE.blueAccent },
};

const stepperSx = {
  mb: 4,
  "& .MuiStepLabel-label.Mui-active": { color: PALETTE.navyMid, fontWeight: 700 },
  "& .MuiStepLabel-label.Mui-completed": { color: PALETTE.blueAccent },
  "& .MuiStepIcon-root.Mui-active": { color: PALETTE.blueAccent },
  "& .MuiStepIcon-root.Mui-completed": { color: PALETTE.navyMid },
};

function getStatusChipSx(status) {
  switch (status) {
    case "În așteptare":
    case "Necesită informații suplimentare":
      return { bgcolor: "#FFF8E1", color: "#B45309", border: "1px solid rgba(255, 193, 7, 0.35)" };
    case "Cerere înregistrată":
      return { bgcolor: "rgba(21, 101, 192, 0.12)", color: PALETTE.blueAccent, border: "1px solid rgba(21, 101, 192, 0.25)" };
    case "Redirecționat":
      return { bgcolor: "rgba(15, 44, 74, 0.08)", color: PALETTE.navyMid, border: "1px solid rgba(15, 44, 74, 0.15)" };
    case "Cerere soluționată":
      return { bgcolor: "rgba(46, 125, 50, 0.1)", color: "#2e7d32", border: "1px solid rgba(46, 125, 50, 0.25)" };
    case "Nevalidat":
      return { bgcolor: "#FFEBEE", color: "#c62828", border: "1px solid rgba(198, 40, 40, 0.25)" };
    default:
      return { bgcolor: PALETTE.surface, color: PALETTE.body, border: `1px solid ${PALETTE.border}` };
  }
}

function VizualizareSesizare() {
  usePageTitle("Verificare status sesizare");
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [codSesizare, setCodSesizare] = useState("");
  const [email, setEmail] = useState("");
  const [codAcces, setCodAcces] = useState("");
  const [codTrimis, setCodTrimis] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minute în secunde
  const [error, setError] = useState("");
  const [sesizareData, setSesizareData] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const useApi = useAPI();
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const response = await useApi.getComplaintFormData();
        setTurnstileSiteKey(response.turnstile_site_key || null);
      } catch (err) {
        console.error("Error fetching form data:", err);
      }
    };

    fetchFormData();
  }, []);

  // Countdown pentru codul de acces
  useEffect(() => {
    let timer;
    if (codTrimis && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (countdown === 0) {
      setCodTrimis(false);
      setError("Codul de acces a expirat. Vă rugăm să solicitați un nou cod.");
    }
    return () => clearInterval(timer);
  }, [codTrimis, countdown]);

  const handleTurnstileExpire = () => {
    setTurnstileToken(null);
    setTurnstileResetKey((key) => key + 1);
  };

  const handleTrimiteCod = async () => {
    if (!codSesizare || !email) {
      setError("Vă rugăm să completați toate câmpurile.");
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setError("Vă rugăm să completați verificarea de securitate.");
      return;
    }

    try{
      const payload = { form_uuid: codSesizare, email };
      if (turnstileToken) {
        payload[TURNSTILE_RESPONSE_FIELD] = turnstileToken;
      }

      await useApi.userCheckComplaintFormEmail(payload);

      setLoading(true);
      setError("");
      setTurnstileToken(null);

      setTimeout(() => {
        setCodTrimis(true);
        setCountdown(300);
        setLoading(false);
      }, 1500);
    } catch (err) {
      setTurnstileToken(null);
      setTurnstileResetKey((key) => key + 1);
      if(err.status === 404) {
        toast.error("Sesizarea nu exista.");
      } else {
        toast.error("Nu s-a putut trimite codul de acces. Încercați din nou.");
      }
    }
  };

  const handleVerificareCod = async () => {
    if (!codAcces) {
      setError("Vă rugăm să introduceți codul de acces.");
      return;
    }

    if (turnstileSiteKey && !turnstileToken) {
      setError("Vă rugăm să completați verificarea de securitate.");
      return;
    }

    setLoading(true);
    setError("");

    try{
      const payload = { form_uuid: codSesizare, code: codAcces };
      if (turnstileToken) {
        payload[TURNSTILE_RESPONSE_FIELD] = turnstileToken;
      }

      const res = await useApi.userCheckComplaintFormCode(payload);
      
      setSesizareData(res);
      setActiveStep(1);
      setTurnstileToken(null);
      setTurnstileResetKey((key) => key + 1);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setTurnstileToken(null);
      setTurnstileResetKey((key) => key + 1);
      toast.error("Codul este gresit sau expirat.");
    }
  };

  const reincarcaStatus = async () => {
    if (turnstileSiteKey && !turnstileToken) {
      toast.error("Vă rugăm să completați verificarea de securitate.");
      return;
    }

    setLoading(true);
    
    try {
      const payload = { form_uuid: codSesizare, code: codAcces };
      if (turnstileToken) {
        payload[TURNSTILE_RESPONSE_FIELD] = turnstileToken;
      }

      const res = await useApi.userCheckComplaintFormCode(payload);
      setSesizareData(res);
      setTurnstileToken(null);
      setTurnstileResetKey((key) => key + 1);
      setLoading(false);
      toast.success("Statusul a fost actualizat cu succes!");
    } catch (err) {
      setLoading(false);
      setTurnstileToken(null);
      setTurnstileResetKey((key) => key + 1);
      toast.error("Eroare la reîncărcarea statusului.");
    }
  };

  // Handle file upload
  const handleBoxClick = () => {
    fileInputRef.current.click();
  };

  const handleFilesChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const uniqueFiles = newFiles.filter(
      nf => !additionalFiles.some(f => f.name === nf.name && f.size === nf.size)
    );
    setAdditionalFiles([...additionalFiles, ...uniqueFiles]);
    e.target.value = null;
  };

  const handleRemoveFile = (idx) => {
    setAdditionalFiles(additionalFiles.filter((_, i) => i !== idx));
  };

  const handleSubmitAdditionalFiles = async () => {
    if (additionalFiles.length === 0) {
      toast.error("Vă rugăm să atașați cel puțin un document.");
      return;
    }

    setLoading(true);
    try {
      // TODO: Implementați apelul API pentru încărcarea documentelor adiționale
      
      // Simulare upload
      setTimeout(() => {
        toast.success("Documentele au fost încărcate cu succes!");
        setAdditionalFiles([]);
        setLoading(false);
      }, 2000);
    } catch (err) {
      setLoading(false);
      toast.error("Eroare la încărcarea documentelor.");
    }
  };

  return (
    <PageLayout>
        <SkipToContent />
        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      <DefaultNavbar
        routes={pageRoutes}
        transparent
        action={{
          type: "external",
          route: "/formular-sesizare",
          label: "Trimite Sesizare",
          color: "info",
        }}
      />
      
      <SoftBox component="main" id="main-content" tabIndex={-1} sx={{ outline: "none" }} mt={18} mb={5}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card sx={{ overflow: "visible" }}>
              <SoftBox p={4}>
                <SoftBox textAlign="center" mb={4}>
                  <SoftTypography variant="h3" fontWeight="bold" sx={{ color: PALETTE.title }}>
                    Verificare Status Sesizare
                  </SoftTypography>
                  <SoftTypography variant="body2" sx={{ color: PALETTE.body }}>
                    Introduceți datele necesare pentru a verifica statusul sesizării dumneavoastră
                  </SoftTypography>
                </SoftBox>
                
                <Stepper activeStep={activeStep} alternativeLabel sx={stepperSx}>
                  <Step>
                    <StepLabel>Autentificare</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Vizualizare Status</StepLabel>
                  </Step>
                </Stepper>
                
                {activeStep === 0 && (
                  <SoftBox>
                    <SoftBox mb={3}>
                      <SoftInput
                        placeholder="Introduceți codul unic de sesizare"
                        value={codSesizare}
                        onChange={(e) => setCodSesizare(e.target.value)}
                        fullWidth
                      />
                    </SoftBox>
                    
                    <SoftBox mb={3}>
                      <SoftInput
                        type="email"
                        placeholder="Introduceți adresa de email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                      />
                    </SoftBox>
                    
                    {!codTrimis ? (
                      <SoftBox textAlign="center">
                        {turnstileSiteKey && (
                          <SoftBox mb={2} display="flex" justifyContent="center">
                            <TurnstileWidget
                              key={`turnstile-email-${turnstileResetKey}`}
                              siteKey={turnstileSiteKey}
                              onVerify={setTurnstileToken}
                              onExpire={handleTurnstileExpire}
                              onError={handleTurnstileExpire}
                            />
                          </SoftBox>
                        )}
                        <SoftButton
                          variant="contained"
                          onClick={handleTrimiteCod}
                          disabled={loading || (turnstileSiteKey && !turnstileToken)}
                          sx={primaryBtnSx}
                        >
                          {loading ? (
                            <CircularProgress size={24} sx={{ color: PALETTE.navy }} />
                          ) : (
                            "Trimite cod de acces"
                          )}
                        </SoftButton>
                      </SoftBox>
                    ) : (
                      <SoftBox>
                        <Alert severity="success" sx={{ mb: 3 }}>
                          Un cod de acces a fost trimis pe adresa de email. Codul este valabil pentru {Math.floor(countdown / 60)}:{countdown % 60 < 10 ? `0${countdown % 60}` : countdown % 60} minute.
                        </Alert>
                        
                        <SoftBox mb={3}>
                          <SoftInput
                            placeholder="Introduceți codul primit pe email"
                            value={codAcces}
                            onChange={(e) => setCodAcces(e.target.value)}
                            fullWidth
                          />
                        </SoftBox>
                        
                        {turnstileSiteKey && (
                          <SoftBox mb={2} display="flex" justifyContent="center">
                            <TurnstileWidget
                              key={`turnstile-code-${turnstileResetKey}`}
                              siteKey={turnstileSiteKey}
                              onVerify={setTurnstileToken}
                              onExpire={handleTurnstileExpire}
                              onError={handleTurnstileExpire}
                            />
                          </SoftBox>
                        )}

                        <SoftBox textAlign="center">
                          <SoftButton
                            variant="contained"
                            onClick={handleVerificareCod}
                            disabled={loading || (turnstileSiteKey && !turnstileToken)}
                            sx={primaryBtnSx}
                          >
                            {loading ? (
                              <CircularProgress size={24} sx={{ color: PALETTE.navy }} />
                            ) : (
                              "Verifică și accesează sesizarea"
                            )}
                          </SoftButton>
                        </SoftBox>
                      </SoftBox>
                    )}
                    
                    {error && (
                      <Alert severity="error" sx={{ mt: 3 }}>
                        {error}
                      </Alert>
                    )}
                  </SoftBox>
                )}
                
                {activeStep === 1 && sesizareData && (
                  <SoftBox>
                    {loading && (
                      <SoftBox display="flex" justifyContent="center" my={3}>
                        <CircularProgress sx={{ color: PALETTE.blueAccent }} />
                      </SoftBox>
                    )}
                    
                    {!loading && (
                      <>
                        <Card variant="outlined" sx={cardSx}>
                          <SoftBox mb={2}>
                            <SoftTypography variant="h5" fontWeight="bold" sx={{ color: PALETTE.title }}>
                              Date generale ale sesizării
                            </SoftTypography>
                          </SoftBox>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <SoftBox mb={1}>
                                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: PALETTE.navyMid }}>
                                  Cod sesizare
                                </SoftTypography>
                                <SoftTypography variant="body2">
                                  {sesizareData.uuid}
                                </SoftTypography>
                              </SoftBox>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <SoftBox mb={1}>
                                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: PALETTE.navyMid }}>
                                  Data depunerii
                                </SoftTypography>
                                <SoftTypography variant="body2">
                                  {formatDate(sesizareData.created_at)}
                                </SoftTypography>
                              </SoftBox>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <SoftBox mb={1}>
                                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: PALETTE.navyMid }}>
                                  Instituția care gestionează sesizarea
                                </SoftTypography>
                                <SoftTypography variant="body2">
                                  {sesizareData.institution_entity}
                                </SoftTypography>
                              </SoftBox>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <SoftBox mb={1}>
                                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: PALETTE.navyMid }}>
                                  Număr de înregistrare intern
                                </SoftTypography>
                                <SoftTypography variant="body2">
                                  {sesizareData.uuid}
                                </SoftTypography>
                              </SoftBox>
                            </Grid>
                          </Grid>
                        </Card>
                        
                        <Card variant="outlined" sx={cardSx}>
                          <SoftBox mb={2}>
                            <SoftTypography variant="h5" fontWeight="bold" sx={{ color: PALETTE.title }}>
                              Status actual al sesizării
                            </SoftTypography>
                          </SoftBox>
                          
                          <SoftBox mb={2}>
                            <Chip 
                              label={sesizareData.status} 
                              size="medium"
                              sx={{ 
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                height: "auto",
                                py: 0.5,
                                ...getStatusChipSx(sesizareData.status),
                              }} 
                            />
                          </SoftBox>
                          
                          <SoftBox mb={2}>
                            {sesizareData.status === "Necesită informații suplimentare" ? (
                              <>
                                <Alert severity="info" sx={{ mb: 2, ...infoAlertSx }}>
                                  <SoftTypography variant="body2" fontWeight="medium" mb={1}>
                                    Mesaj de la administrator:
                                  </SoftTypography>
                                  {sesizareData.status_changes[0].additional_info_details}
                                </Alert>
                                
                                <SoftTypography variant="body2" fontWeight="medium" mb={2} sx={{ color: PALETTE.title }}>
                                  Adăugați documentele solicitate mai jos:
                                </SoftTypography>

                                <SoftBox
                                  p={3}
                                  border={`1px dashed ${PALETTE.border}`}
                                  borderRadius="8px"
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  justifyContent="center"
                                  sx={{
                                    cursor: "pointer",
                                    transition: "all 0.2s ease-in-out",
                                    bgcolor: PALETTE.surface,
                                    "&:hover": { 
                                      bgcolor: "rgba(21, 101, 192, 0.06)",
                                      borderColor: PALETTE.blueAccent,
                                    },
                                  }}
                                  onClick={handleBoxClick}
                                >
                                  <Icon fontSize="large" sx={{ color: PALETTE.blueAccent, mb: 1 }}>cloud_upload</Icon>
                                  <SoftTypography variant="button" fontWeight="regular" sx={{ color: PALETTE.body }}>
                                    Trageți fișierele aici sau click pentru a încărca
                                  </SoftTypography>
                                  <SoftTypography variant="caption" sx={{ color: PALETTE.muted, mt: 0.5 }}>
                                    Puteți atașa documente, fotografii sau fișiere video ca dovezi
                                  </SoftTypography>
                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    style={{ display: "none" }}
                                    onChange={handleFilesChange}
                                    tabIndex={-1}
                                  />
                                </SoftBox>

                                {additionalFiles && additionalFiles.length > 0 && (
                                  <SoftBox mt={2}>
                                    <SoftTypography variant="caption" fontWeight="bold" sx={{ color: PALETTE.body, mb: 1, display: "block" }}>
                                      Fișiere selectate ({additionalFiles.length}):
                                    </SoftTypography>
                                    <SoftBox 
                                      sx={{ 
                                        maxHeight: "150px", 
                                        overflowY: "auto",
                                        border: `1px solid ${PALETTE.border}`,
                                        borderRadius: "6px",
                                        p: 1.5,
                                        bgcolor: "white",
                                      }}
                                    >
                                      {additionalFiles.map((file, idx) => (
                                        <SoftBox 
                                          key={file.name + file.size} 
                                          display="flex" 
                                          alignItems="center" 
                                          justifyContent="space-between"
                                          mb={1}
                                          p={1}
                                          sx={{
                                            bgcolor: PALETTE.surface,
                                            borderRadius: "4px",
                                            "&:hover": { bgcolor: "rgba(21, 101, 192, 0.06)" },
                                          }}
                                        >
                                          <SoftBox display="flex" alignItems="center" flex={1}>
                                            <Icon sx={{ color: PALETTE.muted, fontSize: "1.2rem", mr: 1 }}>
                                              insert_drive_file
                                            </Icon>
                                            <SoftTypography variant="caption" sx={{ color: PALETTE.title }}>
                                              {file.name}
                                            </SoftTypography>
                                            <SoftTypography variant="caption" sx={{ color: PALETTE.muted, ml: 1 }}>
                                              ({(file.size / 1024).toFixed(1)} KB)
                                            </SoftTypography>
                                          </SoftBox>
                                          <Icon
                                            fontSize="small"
                                            sx={{ 
                                              cursor: "pointer", 
                                              color: '#ef4444',
                                              '&:hover': { color: '#dc2626' }
                                            }}
                                            onClick={() => handleRemoveFile(idx)}
                                          >
                                            delete
                                          </Icon>
                                        </SoftBox>
                                      ))}
                                    </SoftBox>
                                    
                                    <SoftBox mt={2} display="flex" justifyContent="flex-end">
                                      <SoftButton
                                        variant="contained"
                                        onClick={handleSubmitAdditionalFiles}
                                        disabled={loading}
                                        sx={primaryBtnSx}
                                      >
                                        {loading ? (
                                          <CircularProgress size={20} sx={{ color: PALETTE.navy }} />
                                        ) : (
                                          "Trimite documente"
                                        )}
                                      </SoftButton>
                                    </SoftBox>
                                  </SoftBox>
                                )}
                              </>
                            ) : (
                              <SoftTypography variant="body2">
                                {sesizareData.statusDetalii || "Nu există detalii suplimentare pentru acest status."}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </Card>
                        
                        {sesizareData.notificari && sesizareData.notificari.length > 0 && (
                          <Card variant="outlined" sx={cardSx}>
                            <SoftBox mb={2}>
                              <SoftTypography variant="h5" fontWeight="bold" sx={{ color: PALETTE.title }}>
                                Notificări importante
                              </SoftTypography>
                            </SoftBox>
                            
                            {sesizareData.notificari.map((notificare, index) => (
                              <Alert severity="info" sx={{ mb: index < sesizareData.notificari.length - 1 ? 2 : 0, ...infoAlertSx }} key={index}>
                                {notificare}
                              </Alert>
                            ))}
                          </Card>
                        )}
                        
                        {sesizareData.files && sesizareData.files.length > 0 && (
                          <Card variant="outlined" sx={sectionCardSx}>
                            <SoftBox mb={2} display="flex" alignItems="center" justifyContent="space-between">
                              <SoftBox display="flex" alignItems="center">
                                <Icon sx={{ color: PALETTE.body, fontSize: "1.5rem", mr: 1 }}>folder</Icon>
                                <SoftTypography variant="h6" fontWeight="bold" sx={{ color: PALETTE.title }}>
                                  Fișiere atașate
                                </SoftTypography>
                              </SoftBox>
                              <Chip 
                                label={`${sesizareData.files.length} ${sesizareData.files.length === 1 ? "document" : "documente"}`}
                                size="small"
                                sx={{ 
                                  bgcolor: "rgba(21, 101, 192, 0.12)", 
                                  color: PALETTE.blueAccent,
                                  fontWeight: "bold",
                                  fontSize: "0.75rem",
                                }}
                              />
                            </SoftBox>
                            
                            <SoftBox>
                              {sesizareData.files.map((doc, index) => (
                                <SoftBox 
                                  key={index} 
                                  sx={{
                                    mb: 1.5,
                                    p: 2,
                                    bgcolor: "white",
                                    border: `1px solid ${PALETTE.border}`,
                                    borderRadius: "6px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      borderColor: PALETTE.blueAccent,
                                      boxShadow: "0 2px 8px rgba(21, 101, 192, 0.15)",
                                      transform: "translateX(2px)",
                                    },
                                  }}
                                >
                                  <SoftBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                                    <SoftBox display="flex" alignItems="center" flex={1} minWidth="0">
                                      <Icon sx={{ color: PALETTE.muted, fontSize: "1.5rem", mr: 1.5, flexShrink: 0 }}>
                                        insert_drive_file
                                      </Icon>
                                      <SoftBox minWidth="0" flex={1} sx={{ lineHeight: 1 }}>
                                        <SoftTypography 
                                          variant="body2" 
                                          fontWeight="medium" 
                                          sx={{ 
                                            color: PALETTE.title,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            lineHeight: "1.2",
                                            mb: 0,
                                            pb: 0,
                                          }}
                                        >
                                          {doc.filename || (doc.file ? decodeURIComponent(doc.file.split("/").pop()) : null) || doc.nume || doc.name || `Document ${index + 1}`}
                                        </SoftTypography>
                                      </SoftBox>
                                    </SoftBox>
                                    
                                    <Button 
                                      variant="outlined"
                                      size="small"
                                      startIcon={<Icon sx={{ fontSize: "1rem" }}>download</Icon>}
                                      onClick={() => {
                                        if (doc.file_base64) {
                                          const link = document.createElement("a");
                                          link.href = `data:application/octet-stream;base64,${doc.file_base64}`;
                                          link.download = doc.filename || `document_${index + 1}${doc.file_extension || ""}`;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        } else {
                                          window.open(doc.file || doc.url || doc.path || "#", "_blank");
                                        }
                                      }}
                                      sx={{
                                        mt: { xs: 1, sm: 0 },
                                        ml: { xs: 0, sm: 2 },
                                        ...downloadBtnSx,
                                      }}
                                    >
                                      Descarcă
                                    </Button>
                                  </SoftBox>
                                </SoftBox>
                              ))}
                            </SoftBox>
                          </Card>
                        )}
                        
                        {sesizareData.description && (
                          <Card variant="outlined" sx={cardSx}>
                            <SoftBox mb={2}>
                              <SoftTypography variant="h5" fontWeight="bold" sx={{ color: PALETTE.title }}>
                                Detalii adiționale
                              </SoftTypography>
                            </SoftBox>
                            
                            <SoftTypography variant="body2" sx={{ color: PALETTE.body }}>
                              {sesizareData.description}
                            </SoftTypography>
                          </Card>
                        )}
                        
                        <Card variant="outlined" sx={sectionCardSx}>
                          <SoftBox mb={2} display="flex" alignItems="center" justifyContent="space-between">
                            <SoftBox display="flex" alignItems="center">
                              <Icon sx={{ color: PALETTE.body, fontSize: "1.5rem", mr: 1 }}>folder</Icon>
                              <SoftTypography variant="h6" fontWeight="bold" sx={{ color: PALETTE.title }}>
                                Documente {sesizareData.status === "Soluționată" ? "finale" : ""}
                              </SoftTypography>
                            </SoftBox>
                            {sesizareData.resolved_form?.files && sesizareData.resolved_form.files.length > 0 && (
                              <Chip 
                                label={`${sesizareData.resolved_form.files.length} ${sesizareData.resolved_form.files.length === 1 ? "document" : "documente"}`}
                                size="small"
                                sx={{ 
                                  bgcolor: "rgba(21, 101, 192, 0.12)", 
                                  color: PALETTE.blueAccent,
                                  fontWeight: "bold",
                                  fontSize: "0.75rem",
                                }}
                              />
                            )}
                          </SoftBox>
                          
                          {sesizareData.resolved_form?.files && sesizareData.resolved_form.files.length > 0 ? (
                            <SoftBox>
                              {sesizareData.resolved_form.files.map((doc, index) => (
                                <SoftBox 
                                  key={index} 
                                  sx={{
                                    mb: 1.5,
                                    p: 2,
                                    bgcolor: "white",
                                    border: `1px solid ${PALETTE.border}`,
                                    borderRadius: "6px",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      borderColor: PALETTE.blueAccent,
                                      boxShadow: "0 2px 8px rgba(21, 101, 192, 0.15)",
                                      transform: "translateX(2px)",
                                    },
                                  }}
                                >
                                  <SoftBox display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                                    <SoftBox display="flex" alignItems="center" flex={1} minWidth="0">
                                      <Icon sx={{ color: PALETTE.muted, fontSize: "1.5rem", mr: 1.5, flexShrink: 0 }}>
                                        insert_drive_file
                                      </Icon>
                                      <SoftBox minWidth="0" flex={1} sx={{ lineHeight: 1 }}>
                                        <SoftTypography 
                                          variant="body2" 
                                          fontWeight="medium" 
                                          sx={{ 
                                            color: PALETTE.title,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            lineHeight: "1.2",
                                            mb: 0,
                                            pb: 0,
                                          }}
                                        >
                                          {doc.filename || (doc.file ? decodeURIComponent(doc.file.split("/").pop()) : null) || doc.nume || doc.name || `Document ${index + 1}`}
                                        </SoftTypography>
                                        {sesizareData.resolved_form.created_at && index === 0 && (
                                          <SoftTypography 
                                            variant="caption" 
                                            sx={{ 
                                              color: PALETTE.muted, 
                                              fontSize: "0.7rem", 
                                              lineHeight: "1",
                                              display: "block",
                                              mt: 0.25,
                                              pt: 0,
                                            }}
                                          >
                                            {formatDate(sesizareData.resolved_form.created_at)}
                                          </SoftTypography>
                                        )}
                                      </SoftBox>
                                    </SoftBox>
                                    
                                    <Button 
                                      variant="outlined"
                                      size="small"
                                      startIcon={<Icon sx={{ fontSize: "1rem" }}>download</Icon>}
                                      onClick={() => {
                                        if (doc.file_base64) {
                                          const link = document.createElement("a");
                                          link.href = `data:application/octet-stream;base64,${doc.file_base64}`;
                                          link.download = doc.filename || `document_${index + 1}${doc.file_extension || ""}`;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                        } else {
                                          window.open(doc.file || doc.url || doc.path || "#", "_blank");
                                        }
                                      }}
                                      sx={{
                                        mt: { xs: 1, sm: 0 },
                                        ml: { xs: 0, sm: 2 },
                                        ...downloadBtnSx,
                                      }}
                                    >
                                      Descarcă
                                    </Button>
                                  </SoftBox>
                                </SoftBox>
                              ))}
                            </SoftBox>
                          ) : (
                            <SoftBox 
                              p={3} 
                              textAlign="center"
                              sx={{
                                bgcolor: "white",
                                borderRadius: "6px",
                                border: `2px dashed ${PALETTE.border}`,
                              }}
                            >
                              <Icon sx={{ fontSize: "2.5rem", color: PALETTE.muted, mb: 1 }}>
                                folder_open
                              </Icon>
                              <SoftTypography variant="body2" sx={{ color: PALETTE.body }}>
                                Nu au fost încărcate documente.
                              </SoftTypography>
                            </SoftBox>
                          )}
                          
                          {sesizareData.resolved_form?.resolution_details && (
                            <SoftBox 
                              mt={2} 
                              p={2} 
                              sx={{
                                bgcolor: "rgba(21, 101, 192, 0.08)",
                                borderRadius: "6px",
                                borderLeft: `3px solid ${PALETTE.blueAccent}`,
                              }}
                            >
                              <SoftBox display="flex" alignItems="center" mb={0.5}>
                                <Icon sx={{ color: PALETTE.blueAccent, fontSize: "1.1rem", mr: 0.75 }}>info</Icon>
                                <SoftTypography variant="caption" fontWeight="bold" sx={{ color: PALETTE.navyMid }}>
                                  Detalii rezoluție
                                </SoftTypography>
                              </SoftBox>
                              <SoftTypography variant="body2" sx={{ color: PALETTE.body, fontSize: "0.875rem" }}>
                                {sesizareData.resolved_form.resolution_details}
                              </SoftTypography>
                            </SoftBox>
                          )}
                        </Card>
                        
                        <SoftBox display="flex" justifyContent="flex-end" alignItems="center" gap={2} flexWrap="wrap">
                          {turnstileSiteKey && (
                            <TurnstileWidget
                              key={`turnstile-reload-${turnstileResetKey}`}
                              siteKey={turnstileSiteKey}
                              onVerify={setTurnstileToken}
                              onExpire={handleTurnstileExpire}
                              onError={handleTurnstileExpire}
                            />
                          )}
                          <SoftButton
                            variant="contained"
                            startIcon={<Icon>refresh</Icon>}
                            onClick={reincarcaStatus}
                            disabled={loading || (turnstileSiteKey && !turnstileToken)}
                            sx={navyBtnSx}
                          >
                            Reîncarcă statusul
                          </SoftButton>
                        </SoftBox>
                      </>
                    )}
                  </SoftBox>
                )}
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      
      <Footer />
    </PageLayout>
  );
}

export default VizualizareSesizare;
